import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isIOS } from '../utils/platform';
import { initializeIAP, retryPendingChecks } from '../services/appleIAP';
import { useAppStore } from '../stores/appStore';

export default function useIAPSetup() {
  const queryClient = useQueryClient();
  const initialized = useRef(false);
  // Set by PrivateRoute (and onboarding) once a session is established: at
  // launch for a signed-in user, and on every sign-in.
  const userSub = useAppStore((s) => s.user?.sub || null);

  useEffect(() => {
    if (!isIOS() || initialized.current) return;
    initialized.current = true;

    initializeIAP({
      onSubscriptionUpdate: (update) => {
        // `verified` path: backend has the latest truth → invalidate to refetch.
        if (update.refetch) {
          queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
          // Baby-scoped entitlement (useEntitlement) derives from the own plan.
          queryClient.invalidateQueries({ queryKey: ['entitlement'] });
          return;
        }
        // `approved` path — ONLY for a purchase the user has just made with
        // store.order (appleIAP never patches for a restore re-delivery):
        // optimistically flip the cache so the UI shows Premium at once. The
        // verified refetch reconciles with the backend. A fresh Apple row
        // outranks a preview or a pass, and nothing has lapsed any more.
        queryClient.setQueryData(['subscription-status'], (prev) => ({
          ...(prev || {}),
          plan: update.plan,
          interval: update.interval,
          provider: update.provider,
          status: update.status,
          currentPeriodEnd: update.currentPeriodEnd,
          isPreview: false,
          previewEndsAt: null,
          isPass: false,
          passEndsAt: null,
          lapsed: null,
        }));
      },
    });
  }, [queryClient]);

  // A purchase whose link failed, or a launch re-delivery, that apple-check
  // could not answer is asked again for the account that made it — at bind
  // (inside initializeIAP) and here, whenever someone signs in.
  useEffect(() => {
    if (!isIOS() || !userSub) return;
    retryPendingChecks().catch(() => {});
  }, [userSub]);
}
